import { DashboardCard } from "./dashboard-card";
import { useAccountContext } from "../account-context";
import { useTradingStats } from "../hooks/use-trading-stats";
import { formatCurrency, cn } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { Progress } from "@/components/ui/progress";
import { type TimeInterval } from "./time-interval-utils";
import { ActivityIcon, ArrowRightIcon } from "lucide-react";
import { Link } from "react-router-dom";

type TradingStatsCardProps = {
  timeInterval?: TimeInterval;
};

export const TradingStatsCard = ({ timeInterval: _timeInterval }: TradingStatsCardProps) => {
  const { fills, isLoading } = useAccountContext();
  const stats = useTradingStats(fills);

  if (isLoading) {
    return (
      <DashboardCard title="Trading Statistics" subtitle="Performance metrics">
        <div className="flex items-center justify-center h-48">
          <Spinner />
        </div>
      </DashboardCard>
    );
  }

  if (!stats.totalTrades) {
    return (
      <DashboardCard title="Trading Statistics" subtitle="Performance metrics" className="h-full">
        <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
          <ActivityIcon className="size-12 mb-3 opacity-50" />
          <p className="text-sm">No trades yet</p>
          <p className="text-xs">Your trading stats will appear here</p>
        </div>
      </DashboardCard>
    );
  }

  return (
    <DashboardCard title="Trading Statistics" subtitle="Performance metrics" className="h-full">
      <div className="space-y-4">
        {/* Win Rate */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Win Rate</span>
            <span
              className={cn(
                "font-bold text-lg",
                stats.winRate >= 50 ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {stats.winRate.toFixed(1)}%
            </span>
          </div>
          <Progress value={stats.winRate} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{stats.winningTrades} wins</span>
            <span>{stats.losingTrades} losses</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-3 pt-2">
          <div className="bg-muted/30 rounded-lg p-3 space-y-1">
            <div className="text-xs text-muted-foreground">
              Total Trades
            </div>
            <div className="text-xl font-bold">{stats.totalTrades}</div>
          </div>

          <div className="bg-muted/30 rounded-lg p-3 space-y-1">
            <div className="text-xs text-muted-foreground">
              Profit Factor
            </div>
            <div
              className={cn(
                "text-xl font-bold",
                stats.profitFactor >= 1 ? "text-bid-foreground" : "text-ask-foreground"
              )}
            >
              {stats.profitFactor === Infinity
                ? "∞"
                : stats.profitFactor.toFixed(2)}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-3 space-y-1">
            <div className="text-xs text-muted-foreground">
              Avg Win
            </div>
            <div className="text-lg font-semibold text-bid-foreground">
              +${formatCurrency(stats.averageProfit, 2)}
            </div>
          </div>

          <div className="bg-muted/30 rounded-lg p-3 space-y-1">
            <div className="text-xs text-muted-foreground">
              Avg Loss
            </div>
            <div className="text-lg font-semibold text-ask-foreground">
              -${formatCurrency(stats.averageLoss, 2)}
            </div>
          </div>
        </div>

        {/* Link to full analysis */}
        <div className="pt-3 border-t border-border/30 mt-3">
          <Link
            to="/performance-lab"
            className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors"
          >
            <span>View full analysis</span>
            <ArrowRightIcon className="size-3" />
          </Link>
        </div>
      </div>
    </DashboardCard>
  );
};
